<template>
  <section class="space-y-5">
    <div>
      <h2 class="text-lg font-semibold text-zinc-800">
        Products
      </h2>
      <p class="mt-1 text-sm text-zinc-500">
        Assign or remove this member's access to incognito product features.
      </p>
    </div>

    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div class="flex items-center justify-between gap-3 border-b border-zinc-200 pb-3">
        <div>
          <p class="text-sm font-semibold text-zinc-800">
            Incognito Feature Access
          </p>
          <p class="mt-1 text-xs text-zinc-500">
            Checked features are visible to this member even when hidden globally.
          </p>
        </div>
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="loading || saving"
          @click="fetchAssignments"
        >
          Refresh
        </button>
      </div>

      <div class="pt-4">
        <div
          v-if="loading"
          class="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-3 text-sm text-zinc-500"
        >
          Loading products...
        </div>

        <div
          v-else-if="errorMessage"
          class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-600"
        >
          {{ errorMessage }}
        </div>

        <div
          v-else-if="!featureRows.length"
          class="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-3 text-sm text-zinc-500"
        >
          No incognito features found. Mark product features as incognito in Product Settings first.
        </div>

        <div
          v-else
          class="space-y-2"
        >
          <label
            v-for="feature in featureRows"
            :key="`member-product-incognito-${feature.instanceId}`"
            class="flex items-start gap-3 rounded-lg border border-zinc-200 px-3 py-2.5 hover:bg-zinc-50 transition-colors"
          >
            <input
              v-model="selectedFeatureIds"
              type="checkbox"
              class="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#2d8bff] focus:ring-0"
              :value="feature.instanceId"
              :disabled="saving"
            >
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-semibold text-zinc-800">
                {{ feature.displayName }}
              </p>
              <p class="truncate text-xs text-zinc-500">
                {{ feature.slugLabel }}
              </p>
            </div>
          </label>
        </div>
      </div>

      <div class="mt-4 flex items-center gap-2 border-t border-zinc-200 pt-3">
        <button
          type="button"
          class="inline-flex items-center rounded-md bg-[#2D8CFF] px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#2D8CFF]/90 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="loading || saving || !isDirty"
          @click="saveAssignments"
        >
          {{ saving ? 'Saving...' : 'Save Access' }}
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="loading || saving || !isDirty"
          @click="resetSelection"
        >
          Reset
        </button>
        <p
          v-if="savedMessage"
          class="text-xs font-medium text-emerald-600"
        >
          {{ savedMessage }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  memberId: {
    type: [String, Number],
    default: null,
  },
});

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const loading = ref(false);
const saving = ref(false);
const errorMessage = ref('');
const savedMessage = ref('');
const featureRows = ref([]);
const selectedFeatureIds = ref([]);
const initialFeatureIds = ref([]);

const normalizeFeatureIds = (input) => {
  if (!Array.isArray(input)) return [];
  const ids = [];
  const seen = new Set();
  for (const raw of input) {
    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

const isDirty = computed(() => {
  const current = normalizeFeatureIds(selectedFeatureIds.value).sort((a, b) => a - b);
  const initial = normalizeFeatureIds(initialFeatureIds.value).sort((a, b) => a - b);
  if (current.length !== initial.length) return true;
  return current.some((id, idx) => id !== initial[idx]);
});

const mapRows = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((feature) => {
      const instanceId = Number(feature?.instanceId ?? feature?.instance_id ?? 0);
      if (!Number.isInteger(instanceId) || instanceId <= 0) return null;
      const displayName = String(
        feature?.customName ??
        feature?.custom_name ??
        feature?.featureName ??
        feature?.feature_name ??
        feature?.slug ??
        `Feature ${instanceId}`
      ).trim() || `Feature ${instanceId}`;
      const slug = String(feature?.slug || '').trim();
      return {
        instanceId,
        displayName,
        slugLabel: slug ? `/${slug}` : `Instance #${instanceId}`,
      };
    })
    .filter(Boolean);

const fetchAssignments = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) {
    featureRows.value = [];
    selectedFeatureIds.value = [];
    initialFeatureIds.value = [];
    return;
  }

  loading.value = true;
  errorMessage.value = '';
  savedMessage.value = '';
  try {
    const response = await fetch(
      `${apiProxyUrl}/api/members/${encodeURIComponent(String(memberId))}/incognito-features`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body?.success === false) {
      throw new Error(String(body?.message || body?.error || `Failed loading assignments (${response.status})`));
    }

    const rows = mapRows(body?.features);
    const assigned = normalizeFeatureIds(
      body?.assignedFeatureInstanceIds ?? body?.assigned_feature_instance_ids ?? []
    );
    const validIds = new Set(rows.map((r) => r.instanceId));
    const filteredAssigned = assigned.filter((id) => validIds.has(id));

    featureRows.value = rows;
    selectedFeatureIds.value = [...filteredAssigned];
    initialFeatureIds.value = [...filteredAssigned];
  } catch (err) {
    featureRows.value = [];
    selectedFeatureIds.value = [];
    initialFeatureIds.value = [];
    errorMessage.value = String(err?.message || 'Failed loading products.');
  } finally {
    loading.value = false;
  }
};

const resetSelection = () => {
  selectedFeatureIds.value = [...normalizeFeatureIds(initialFeatureIds.value)];
  savedMessage.value = '';
};

const saveAssignments = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) return;

  saving.value = true;
  errorMessage.value = '';
  savedMessage.value = '';
  try {
    const payloadIds = normalizeFeatureIds(selectedFeatureIds.value);
    const response = await fetch(
      `${apiProxyUrl}/api/members/${encodeURIComponent(String(memberId))}/incognito-features`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          featureInstanceIds: payloadIds,
        }),
      }
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body?.success === false) {
      throw new Error(String(body?.message || body?.error || `Failed saving assignments (${response.status})`));
    }

    const savedIds = normalizeFeatureIds(
      body?.assignedFeatureInstanceIds ?? body?.assigned_feature_instance_ids ?? payloadIds
    );
    initialFeatureIds.value = [...savedIds];
    selectedFeatureIds.value = [...savedIds];
    savedMessage.value = 'Product access updated.';
  } catch (err) {
    errorMessage.value = String(err?.message || 'Failed saving products.');
  } finally {
    saving.value = false;
  }
};

watch(
  () => props.memberId,
  () => {
    void fetchAssignments();
  },
  { immediate: true }
);
</script>
